import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import type { GetLeadsByAddressQuery } from '@graphql/__generated__/graphql';
import {
  ADDRESS_SEARCH_DEBOUNCE_MS,
  type AddressSearchInput,
  canSearchByAddress,
} from '@modules/_shared/utils/addressSearch';
import { GET_LEADS_BY_ADDRESS } from '@modules/reports/cma-reports/cmaReportsQueries';
import { useDebouncedValue } from '@shared/hooks/debounce';

export type LeadAtAddress =
  GetLeadsByAddressQuery['find_cma_leads_by_address'][number];

type UseLeadsByAddressResult = {
  leads: LeadAtAddress[];
  loading: boolean;
  error: ErrorLike | null;
};

export const useLeadsByAddress = (
  address: AddressSearchInput | null | undefined,
): UseLeadsByAddressResult => {
  const debouncedAddress = useDebouncedValue(
    address,
    ADDRESS_SEARCH_DEBOUNCE_MS,
  );

  const canSearch = canSearchByAddress(debouncedAddress);
  const route = debouncedAddress?.route?.trim() ?? '';
  const streetNumber = debouncedAddress?.street_number?.trim() ?? '';
  const postcode = debouncedAddress?.postcode?.trim() || null;
  const locality = debouncedAddress?.locality?.trim() || null;

  const { data, loading, error } = useQuery(GET_LEADS_BY_ADDRESS, {
    skip: !canSearch,
    variables: { route, streetNumber, postcode, locality },
    fetchPolicy: 'cache-and-network',
  });

  const addressChanged = address !== debouncedAddress;

  return {
    leads:
      canSearch && !addressChanged
        ? ((data?.find_cma_leads_by_address ?? []) as LeadAtAddress[])
        : [],
    loading: loading || (addressChanged && canSearchByAddress(address)),
    error: error ?? null,
  };
};
